import React, { useState } from "react";
import { Box, Tab, Tabs, ThemeProvider, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { SetupPageContainer } from "../../../utils/commonStyledComponents/commonStyled";
import { messages } from "../../../locales/messages";
import RecommendationEnginePipelineEditorPanel from "./RecommendationEnginePipelineEditorPanel";
import RecommendationEngineGlobalSettings from "./components/RecommendationEngineGlobalSettings";
import InfoTooltip from "./components/InfoTooltip";
import {
  RECOMMENDATION_ENGINE_ENDPOINT_LABELS,
  RECOMMENDATION_ENGINE_ENDPOINT_ORDER,
} from "../../../utils/recommendationEngine/constants";
import { RecommendationEngineEndpoint } from "../../../types/recommendationEngine";
import {
  SIGNAL_CONCEPT_TOOLTIPS,
  TOP_OF_PAGE_EXPLAINER,
} from "./copy";
import { engineTheme, engineTokens } from "./theme";

const GlossaryTooltipBody = () => (
  <Box>
    {Object.entries({
      Signal: SIGNAL_CONCEPT_TOOLTIPS.signal,
      "Normalized signal": SIGNAL_CONCEPT_TOOLTIPS.normalizedSignal,
      "Raw signal": SIGNAL_CONCEPT_TOOLTIPS.rawSignal,
      "Applicable endpoints": SIGNAL_CONCEPT_TOOLTIPS.applicableEndpoints,
      "Signal group": SIGNAL_CONCEPT_TOOLTIPS.signalGroup,
      "Matched sources": SIGNAL_CONCEPT_TOOLTIPS.matchedSources,
      "Final score": SIGNAL_CONCEPT_TOOLTIPS.finalScore,
    }).map(([term, description]) => (
      <Box key={term} sx={{ mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {term}:
        </Typography>{" "}
        <Typography variant="caption">{description}</Typography>
      </Box>
    ))}
  </Box>
);

const RecommendationEnginePage = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<
    RecommendationEngineEndpoint | "global_settings"
  >("recommended");

  return (
    <ThemeProvider theme={engineTheme}>
      <SetupPageContainer data-testid="recommendation-engine-page">
        <AccountCardWrapper title={t(messages.RecommendationEngine_Title())}>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                borderLeft: `3px solid ${engineTokens.brand}`,
                pl: 1.5,
                mb: 2,
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                {TOP_OF_PAGE_EXPLAINER}
              </Typography>
              <InfoTooltip title={<GlossaryTooltipBody />} />
            </Box>

            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {RECOMMENDATION_ENGINE_ENDPOINT_ORDER.map((endpoint) => (
                <Tab
                  key={endpoint}
                  value={endpoint}
                  label={RECOMMENDATION_ENGINE_ENDPOINT_LABELS[
                    endpoint
                  ].replace(" configuration", "")}
                  data-testid={`recommendation-engine-tab-${endpoint}`}
                />
              ))}
              <Tab
                value="global_settings"
                label="Global settings"
                data-testid="recommendation-engine-tab-settings"
              />
            </Tabs>

            {RECOMMENDATION_ENGINE_ENDPOINT_ORDER.includes(
              tab as RecommendationEngineEndpoint,
            ) && (
              <RecommendationEnginePipelineEditorPanel
                endpoint={tab as RecommendationEngineEndpoint}
              />
            )}
            {tab === "global_settings" && (
              <RecommendationEngineGlobalSettings />
            )}
          </Box>
        </AccountCardWrapper>
      </SetupPageContainer>
    </ThemeProvider>
  );
};

export default RecommendationEnginePage;
